import { Language } from '../types';

export interface MotivatorTranslations {
  curiosity: { name: string; description: string };
  honor: { name: string; description: string };
  acceptance: { name: string; description: string };
  mastery: { name: string; description: string };
  power: { name: string; description: string };
  freedom: { name: string; description: string };
  relatedness: { name: string; description: string };
  order: { name: string; description: string };
  goal: { name: string; description: string };
  status: { name: string; description: string };
}

export interface UITranslations {
  home: {
    title: string;
    subtitle: string;
    createRoomTitle: string;
    createRoomDescription: string;
    createRoom: string;
    joinRoomTitle: string;
    joinRoomDescription: string;
    joinRoom: string;
    whatIsTitle: string;
    description1: string;
    description2: string;
  };
  createRoom: {
    title: string;
    subtitle: string;
    roomName: string;
    roomNamePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    visibility: string;
    visibilityPublic: string;
    visibilityPrivate: string;
    create: string;
    cancel: string;
  };
  joinRoom: {
    title: string;
    subtitle: string;
    roomCode: string;
    roomCodePlaceholder: string;
    playerName: string;
    playerNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    join: string;
    cancel: string;
  };
  play: {
    title: string;
    subtitle: string;
    mostImportant: string;
    leastImportant: string;
    saveRanking: string;
    reset: string;
  };
}

export const motivatorTranslations: Record<Language, MotivatorTranslations> = {
  en: {
    curiosity: {
      name: 'Curiosity',
      description: 'I have plenty of things to investigate and to think about.',
    },
    honor: {
      name: 'Honor',
      description: 'I feel proud that my personal values are reflected in how I work.',
    },
    acceptance: {
      name: 'Acceptance',
      description: 'The people around me approve of what I do and who I am.',
    },
    mastery: {
      name: 'Mastery',
      description: 'My work challenges my competence but it is still within my abilities.',
    },
    power: {
      name: 'Power',
      description: 'There\'s enough room for me to influence what happens around me.',
    },
    freedom: {
      name: 'Freedom',
      description: 'I am independent of others with my work and my responsibilities.',
    },
    relatedness: {
      name: 'Relatedness',
      description: 'I have good social contacts with the people in my work.',
    },
    order: {
      name: 'Order',
      description: 'There are enough rules and policies for a stable environment.',
    },
    goal: {
      name: 'Goal',
      description: 'My purpose in life is reflected in the work that I do.',
    },
    status: {
      name: 'Status',
      description: 'My position is good, and recognized by the people who work with me.',
    },
  },
  pt: {
    curiosity: {
      name: 'Curiosidade',
      description: 'Tenho muitas coisas para investigar e pensar.',
    },
    honor: {
      name: 'Honra',
      description: 'Sinto orgulho de que meus valores pessoais se refletem em como trabalho.',
    },
    acceptance: {
      name: 'Aceitação',
      description: 'As pessoas ao meu redor aprovam o que faço e quem sou.',
    },
    mastery: {
      name: 'Maestria',
      description: 'Meu trabalho desafia minha competência, mas ainda está dentro das minhas habilidades.',
    },
    power: {
      name: 'Poder',
      description: 'Tenho espaço suficiente para influenciar o que acontece ao meu redor.',
    },
    freedom: {
      name: 'Liberdade',
      description: 'Sou independente dos outros com meu trabalho e minhas responsabilidades.',
    },
    relatedness: {
      name: 'Relacionamento',
      description: 'Tenho bons contatos sociais com as pessoas no meu trabalho.',
    },
    order: {
      name: 'Ordem',
      description: 'Existem regras e políticas suficientes para um ambiente estável.',
    },
    goal: {
      name: 'Objetivo',
      description: 'Meu propósito de vida se reflete no trabalho que faço.',
    },
    status: {
      name: 'Status',
      description: 'Minha posição é boa e reconhecida pelas pessoas que trabalham comigo.',
    },
  },
  es: {
    curiosity: {
      name: 'Curiosidad',
      description: 'Tengo muchas cosas para investigar y pensar.',
    },
    honor: {
      name: 'Honor',
      description: 'Me siento orgulloso de que mis valores personales se reflejan en cómo trabajo.',
    },
    acceptance: {
      name: 'Aceptación',
      description: 'Las personas a mi alrededor aprueban lo que hago y quién soy.',
    },
    mastery: {
      name: 'Maestría',
      description: 'Mi trabajo desafía mi competencia pero aún está dentro de mis habilidades.',
    },
    power: {
      name: 'Poder',
      description: 'Tengo suficiente espacio para influir en lo que sucede a mi alrededor.',
    },
    freedom: {
      name: 'Libertad',
      description: 'Soy independiente de otros con mi trabajo y mis responsabilidades.',
    },
    relatedness: {
      name: 'Relación',
      description: 'Tengo buenos contactos sociales con las personas en mi trabajo.',
    },
    order: {
      name: 'Orden',
      description: 'Hay suficientes reglas y políticas para un ambiente estable.',
    },
    goal: {
      name: 'Objetivo',
      description: 'Mi propósito en la vida se refleja en el trabajo que hago.',
    },
    status: {
      name: 'Estatus',
      description: 'Mi posición es buena y reconocida por las personas que trabajan conmigo.',
    },
  },
  fr: {
    curiosity: {
      name: 'Curiosité',
      description: 'J\'ai beaucoup de choses à enquêter et à penser.',
    },
    honor: {
      name: 'Honneur',
      description: 'Je suis fier que mes valeurs personnelles se reflètent dans ma façon de travailler.',
    },
    acceptance: {
      name: 'Acceptation',
      description: 'Les gens autour de moi approuvent ce que je fais et qui je suis.',
    },
    mastery: {
      name: 'Maîtrise',
      description: 'Mon travail défie ma compétence mais reste dans mes capacités.',
    },
    power: {
      name: 'Pouvoir',
      description: 'J\'ai assez d\'espace pour influencer ce qui se passe autour de moi.',
    },
    freedom: {
      name: 'Liberté',
      description: 'Je suis indépendant des autres avec mon travail et mes responsabilités.',
    },
    relatedness: {
      name: 'Relation',
      description: 'J\'ai de bons contacts sociaux avec les gens dans mon travail.',
    },
    order: {
      name: 'Ordre',
      description: 'Il y a suffisamment de règles et de politiques pour un environnement stable.',
    },
    goal: {
      name: 'Objectif',
      description: 'Mon but dans la vie se reflète dans le travail que je fais.',
    },
    status: {
      name: 'Statut',
      description: 'Ma position est bonne et reconnue par les gens qui travaillent avec moi.',
    },
  },
  de: {
    curiosity: {
      name: 'Neugier',
      description: 'Ich habe viele Dinge zu untersuchen und nachzudenken.',
    },
    honor: {
      name: 'Ehre',
      description: 'Ich bin stolz, dass meine persönlichen Werte sich in meiner Arbeit widerspiegeln.',
    },
    acceptance: {
      name: 'Akzeptanz',
      description: 'Die Menschen um mich herum genehmigen, was ich tue und wer ich bin.',
    },
    mastery: {
      name: 'Meisterschaft',
      description: 'Meine Arbeit fordert meine Kompetenz heraus, liegt aber noch in meinen Fähigkeiten.',
    },
    power: {
      name: 'Macht',
      description: 'Es gibt genug Raum für mich, um zu beeinflussen, was um mich herum passiert.',
    },
    freedom: {
      name: 'Freiheit',
      description: 'Ich bin unabhängig von anderen mit meiner Arbeit und meinen Verantwortlichkeiten.',
    },
    relatedness: {
      name: 'Beziehung',
      description: 'Ich habe gute soziale Kontakte zu den Menschen in meiner Arbeit.',
    },
    order: {
      name: 'Ordnung',
      description: 'Es gibt genügend Regeln und Richtlinien für eine stabile Umgebung.',
    },
    goal: {
      name: 'Ziel',
      description: 'Mein Lebenszweck spiegelt sich in der Arbeit wider, die ich tue.',
    },
    status: {
      name: 'Status',
      description: 'Meine Position ist gut und wird von den Menschen anerkannt, mit denen ich arbeite.',
    },
  },
};

export const uiTranslations: Record<Language, UITranslations> = {
  en: {
    home: {
      title: 'Moving Motivators',
      subtitle: 'Discover what truly motivates you and your team',
      createRoomTitle: 'Create a Room',
      createRoomDescription: 'Start a new session and invite your team',
      createRoom: 'Create Room',
      joinRoomTitle: 'Join a Room',
      joinRoomDescription: 'Enter a room code to join an existing session',
      joinRoom: 'Join Room',
      whatIsTitle: 'What is Moving Motivators?',
      description1: 'Moving Motivators is a game that helps you understand what drives you and your team. Based on the CHAMPFROGS model (Curiosity, Honor, Acceptance, Mastery, Power, Freedom, Relatedness, Order, Goal, Status), this exercise reveals the intrinsic motivators that influence behavior and decision-making.',
      description2: 'By ranking these ten cards according to their importance to you, you\'ll gain insights into what truly matters in your work life. This understanding can lead to better team collaboration, more effective leadership, and increased job satisfaction.',
    },
    createRoom: {
      title: 'Create a Room',
      subtitle: 'Set up your Moving Motivators session',
      roomName: 'Room Name',
      roomNamePlaceholder: 'Enter room name',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      visibility: 'Visibility',
      visibilityPublic: 'Public',
      visibilityPrivate: 'Private',
      create: 'Create Room',
      cancel: 'Cancel',
    },
    joinRoom: {
      title: 'Join a Room',
      subtitle: 'Enter the room code to join the session',
      roomCode: 'Room Code',
      roomCodePlaceholder: 'ABC123XYZ',
      playerName: 'Your Name',
      playerNamePlaceholder: 'Enter your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      join: 'Join Room',
      cancel: 'Cancel',
    },
    play: {
      title: 'Rank Your Motivators',
      subtitle: 'Drag and drop the cards to rank them by importance',
      mostImportant: 'Most Important',
      leastImportant: 'Least Important',
      saveRanking: 'Save Ranking',
      reset: 'Reset',
    },
  },
  pt: {
    home: {
      title: 'Moving Motivators',
      subtitle: 'Descubra o que realmente motiva você e sua equipe',
      createRoomTitle: 'Criar uma Sala',
      createRoomDescription: 'Inicie uma nova sessão e convide sua equipe',
      createRoom: 'Criar Sala',
      joinRoomTitle: 'Entrar em uma Sala',
      joinRoomDescription: 'Digite o código da sala para entrar em uma sessão existente',
      joinRoom: 'Entrar na Sala',
      whatIsTitle: 'O que é Moving Motivators?',
      description1: 'Moving Motivators é um jogo que ajuda você a entender o que motiva você e sua equipe. Baseado no modelo CHAMPFROGS (Curiosity, Honor, Acceptance, Mastery, Power, Freedom, Relatedness, Order, Goal, Status), este exercício revela os motivadores intrínsecos que influenciam o comportamento e a tomada de decisões.',
      description2: 'Ao classificar estes dez cartões de acordo com sua importância para você, você obterá insights sobre o que realmente importa em sua vida profissional. Esse entendimento pode levar a uma melhor colaboração em equipe, liderança mais eficaz e maior satisfação no trabalho.',
    },
    createRoom: {
      title: 'Criar uma Sala',
      subtitle: 'Configure sua sessão Moving Motivators',
      roomName: 'Nome da Sala',
      roomNamePlaceholder: 'Digite o nome da sala',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'seu@email.com',
      visibility: 'Visibilidade',
      visibilityPublic: 'Público',
      visibilityPrivate: 'Privado',
      create: 'Criar Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Entrar em uma Sala',
      subtitle: 'Digite o código da sala para entrar na sessão',
      roomCode: 'Código da Sala',
      roomCodePlaceholder: 'ABC123XYZ',
      playerName: 'Seu Nome',
      playerNamePlaceholder: 'Digite seu nome',
      email: 'Email',
      emailPlaceholder: 'seu@email.com',
      join: 'Entrar na Sala',
      cancel: 'Cancelar',
    },
    play: {
      title: 'Classifique Seus Motivadores',
      subtitle: 'Arraste e solte os cartões para classificá-los por importância',
      mostImportant: 'Mais Importante',
      leastImportant: 'Menos Importante',
      saveRanking: 'Salvar Classificação',
      reset: 'Redefinir',
    },
  },
  es: {
    home: {
      title: 'Moving Motivators',
      subtitle: 'Descubre qué te motiva realmente a ti y a tu equipo',
      createRoomTitle: 'Crear una Sala',
      createRoomDescription: 'Inicia una nueva sesión e invita a tu equipo',
      createRoom: 'Crear Sala',
      joinRoomTitle: 'Unirse a una Sala',
      joinRoomDescription: 'Ingresa el código de la sala para unirte a una sesión existente',
      joinRoom: 'Unirse a la Sala',
      whatIsTitle: '¿Qué es Moving Motivators?',
      description1: 'Moving Motivators es un juego que te ayuda a entender qué te impulsa a ti y a tu equipo. Basado en el modelo CHAMPFROGS (Curiosity, Honor, Acceptance, Mastery, Power, Freedom, Relatedness, Order, Goal, Status), este ejercicio revela los motivadores intrínsecos que influyen en el comportamiento y la toma de decisiones.',
      description2: 'Al clasificar estas diez tarjetas según su importancia para ti, obtendrás información sobre lo que realmente importa en tu vida laboral. Este entendimiento puede conducir a una mejor colaboración en el equipo, un liderazgo más efectivo y una mayor satisfacción laboral.',
    },
    createRoom: {
      title: 'Crear una Sala',
      subtitle: 'Configura tu sesión Moving Motivators',
      roomName: 'Nombre de la Sala',
      roomNamePlaceholder: 'Ingresa el nombre de la sala',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'tu@email.com',
      visibility: 'Visibilidad',
      visibilityPublic: 'Público',
      visibilityPrivate: 'Privado',
      create: 'Crear Sala',
      cancel: 'Cancelar',
    },
    joinRoom: {
      title: 'Unirse a una Sala',
      subtitle: 'Ingresa el código de la sala para unirte a la sesión',
      roomCode: 'Código de la Sala',
      roomCodePlaceholder: 'ABC123XYZ',
      playerName: 'Tu Nombre',
      playerNamePlaceholder: 'Ingresa tu nombre',
      email: 'Email',
      emailPlaceholder: 'tu@email.com',
      join: 'Unirse a la Sala',
      cancel: 'Cancelar',
    },
    play: {
      title: 'Clasifica Tus Motivadores',
      subtitle: 'Arrastra y suelta las tarjetas para clasificarlas por importancia',
      mostImportant: 'Más Importante',
      leastImportant: 'Menos Importante',
      saveRanking: 'Guardar Clasificación',
      reset: 'Restablecer',
    },
  },
  fr: {
    home: {
      title: 'Moving Motivators',
      subtitle: 'Découvrez ce qui vous motive vraiment, vous et votre équipe',
      createRoomTitle: 'Créer une Salle',
      createRoomDescription: 'Démarrez une nouvelle session et invitez votre équipe',
      createRoom: 'Créer une Salle',
      joinRoomTitle: 'Rejoindre une Salle',
      joinRoomDescription: 'Entrez le code de la salle pour rejoindre une session existante',
      joinRoom: 'Rejoindre la Salle',
      whatIsTitle: 'Qu\'est-ce que Moving Motivators ?',
      description1: 'Moving Motivators est un jeu qui vous aide à comprendre ce qui vous motive, vous et votre équipe. Basé sur le modèle CHAMPFROGS (Curiosity, Honor, Acceptance, Mastery, Power, Freedom, Relatedness, Order, Goal, Status), cet exercice révèle les motivateurs intrinsèques qui influencent le comportement et la prise de décision.',
      description2: 'En classant ces dix cartes selon leur importance pour vous, vous obtiendrez des informations sur ce qui compte vraiment dans votre vie professionnelle. Cette compréhension peut conduire à une meilleure collaboration en équipe, un leadership plus efficace et une plus grande satisfaction au travail.',
    },
    createRoom: {
      title: 'Créer une Salle',
      subtitle: 'Configurez votre session Moving Motivators',
      roomName: 'Nom de la Salle',
      roomNamePlaceholder: 'Entrez le nom de la salle',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'votre@email.com',
      visibility: 'Visibilité',
      visibilityPublic: 'Public',
      visibilityPrivate: 'Privé',
      create: 'Créer la Salle',
      cancel: 'Annuler',
    },
    joinRoom: {
      title: 'Rejoindre une Salle',
      subtitle: 'Entrez le code de la salle pour rejoindre la session',
      roomCode: 'Code de la Salle',
      roomCodePlaceholder: 'ABC123XYZ',
      playerName: 'Votre Nom',
      playerNamePlaceholder: 'Entrez votre nom',
      email: 'Email',
      emailPlaceholder: 'votre@email.com',
      join: 'Rejoindre la Salle',
      cancel: 'Annuler',
    },
    play: {
      title: 'Classez Vos Motivateurs',
      subtitle: 'Glissez-déposez les cartes pour les classer par importance',
      mostImportant: 'Plus Important',
      leastImportant: 'Moins Important',
      saveRanking: 'Enregistrer le Classement',
      reset: 'Réinitialiser',
    },
  },
  de: {
    home: {
      title: 'Moving Motivators',
      subtitle: 'Entdecken Sie, was Sie und Ihr Team wirklich motiviert',
      createRoomTitle: 'Raum erstellen',
      createRoomDescription: 'Starten Sie eine neue Sitzung und laden Sie Ihr Team ein',
      createRoom: 'Raum erstellen',
      joinRoomTitle: 'Einem Raum beitreten',
      joinRoomDescription: 'Geben Sie den Raumcode ein, um einer bestehenden Sitzung beizutreten',
      joinRoom: 'Raum beitreten',
      whatIsTitle: 'Was ist Moving Motivators?',
      description1: 'Moving Motivators ist ein Spiel, das Ihnen hilft zu verstehen, was Sie und Ihr Team antreibt. Basierend auf dem CHAMPFROGS-Modell (Curiosity, Honor, Acceptance, Mastery, Power, Freedom, Relatedness, Order, Goal, Status) zeigt diese Übung die intrinsischen Motivatoren, die Verhalten und Entscheidungsfindung beeinflussen.',
      description2: 'Wenn Sie diese zehn Karten nach ihrer Bedeutung für Sie ordnen, erhalten Sie Einblicke in das, was in Ihrem Arbeitsleben wirklich zählt. Dieses Verständnis kann zu besserer Teamzusammenarbeit, wirksamerer Führung und höherer Arbeitszufriedenheit führen.',
    },
    createRoom: {
      title: 'Einen Raum Erstellen',
      subtitle: 'Richten Sie Ihre Moving Motivators-Sitzung ein',
      roomName: 'Raumname',
      roomNamePlaceholder: 'Geben Sie den Raumnamen ein',
      playerName: 'Dein Name',
      playerNamePlaceholder: 'Gib deinen Namen ein',
      email: 'Email',
      emailPlaceholder: 'ihre@email.com',
      visibility: 'Sichtbarkeit',
      visibilityPublic: 'Öffentlich',
      visibilityPrivate: 'Privat',
      create: 'Raum Erstellen',
      cancel: 'Abbrechen',
    },
    joinRoom: {
      title: 'Einem Raum beitreten',
      subtitle: 'Geben Sie den Raumcode ein, um der Sitzung beizutreten',
      roomCode: 'Raumcode',
      roomCodePlaceholder: 'ABC123XYZ',
      playerName: 'Dein Name',
      playerNamePlaceholder: 'Gib deinen Namen ein',
      email: 'Email',
      emailPlaceholder: 'ihre@email.com',
      join: 'Raum beitreten',
      cancel: 'Abbrechen',
    },
    play: {
      title: 'Ordnen Sie Ihre Motivatoren',
      subtitle: 'Ziehen und ablegen Sie die Karten, um sie nach Wichtigkeit zu ordnen',
      mostImportant: 'Wichtigste',
      leastImportant: 'Unwichtigste',
      saveRanking: 'Rangfolge Speichern',
      reset: 'Zurücksetzen',
    },
  },
};

export const getMotivatorTranslation = (language: Language, motivatorId: string) => {
  return motivatorTranslations[language][motivatorId as keyof MotivatorTranslations];
};

export const getUITranslation = (language: Language) => {
  return uiTranslations[language];
};
